/**
 * Persists the agent chat conversation (session id + message history) to
 * sessionStorage, so closing/reopening the panel doesn't lose it.
 *
 * sessionStorage (not localStorage) is the right choice here for two
 * reasons that line up exactly with what's actually needed:
 *   - It's wiped when the tab/browser closes, matching "for this session"
 *     rather than persisting indefinitely across visits.
 *   - UserAccount.js's logoutHandler already calls `sessionStorage.clear()`
 *     (to clear MSAL's own cache) — storing the conversation here means
 *     logout already clears it too, for free, with no new hook into the
 *     logout flow required.
 */

const STORAGE_KEY_PREFIX = "astt-agent-conversation";

// ASTT-7496: keys are namespaced per agent backend (e.g. "adk" vs
// "copilot") so AgentChatPanel.js and CopilotChatPanel.js can never
// collide if both are ever shown to the same user at once. Previously a
// single shared key meant whichever panel last wrote to sessionStorage
// silently overwrote the other's conversation -- self-flagged as a known
// risk in this file's own history, currently low-impact only because both
// panels are gated to the same small evaluator allowlist today.
function buildKey(namespace) {
  return `${STORAGE_KEY_PREFIX}:${namespace}`;
}

const CONVERSATION_UPDATED_EVENT = "astt-agent-conversation-updated";

/**
 * ASTT-7496 (B2): lets a mounted panel learn about a conversation update
 * written by a DIFFERENT instance of itself in the same tab -- specifically,
 * a previous mount whose in-flight request was still resolving when the
 * panel was closed and then reopened before that response arrived. Ignores
 * updates whose writerId matches its own (the normal, same-instance case
 * where the panel is simply reacting to its own just-completed request,
 * which already updates its own React state directly -- reacting to the
 * event too would be redundant and could race with startReveal's own
 * setMessages call).
 *
 * Returns an unsubscribe function, matching the useEffect cleanup pattern.
 */
export function subscribeToConversationUpdates(namespace, writerId, onUpdate) {
  const handler = (event) => {
    const detail = event.detail ?? {};
    if (detail.namespace !== namespace) return;
    if (detail.writerId === writerId) return;
    onUpdate(detail.sessionId);
  };
  window.addEventListener(CONVERSATION_UPDATED_EVENT, handler);
  return () => window.removeEventListener(CONVERSATION_UPDATED_EVENT, handler);
}

/** Returns { sessionId, messages } if a valid conversation is stored, or
 * null if there's nothing stored or it's malformed (corrupt/old-schema
 * data should never crash the panel — just start fresh). */
export function loadConversation(namespace) {
  try {
    const raw = sessionStorage.getItem(buildKey(namespace));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.sessionId !== "string" || !Array.isArray(parsed.messages)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveConversation(namespace, sessionId, messages, writerId) {
  try {
    sessionStorage.setItem(buildKey(namespace), JSON.stringify({ sessionId, messages }));
    // ASTT-7496 (B2): notifies any OTHER mounted panel instance in the SAME
    // tab that this namespace's stored conversation just changed --
    // sessionStorage's native "storage" event never fires for writes made
    // in the SAME document (only other tabs/windows), so without this, a
    // same-tab remount (close the panel mid-request, reopen BEFORE the
    // response arrives) would never learn a background write happened
    // until closed and reopened again a second time. writerId lets a
    // listener ignore events it caused itself -- see
    // subscribeToConversationUpdates below.
    window.dispatchEvent(
      new CustomEvent(CONVERSATION_UPDATED_EVENT, { detail: { namespace, sessionId, writerId } })
    );
  } catch {
    // sessionStorage can throw (quota exceeded, private-browsing restrictions
    // in some browsers) — losing persistence is a minor degradation, not
    // worth surfacing to the user or breaking the chat over.
  }
}

export function clearConversation(namespace) {
  try {
    sessionStorage.removeItem(buildKey(namespace));
  } catch {
    // see saveConversation
  }
}
